import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { ListComponent } from '../list/list.component';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { CognitoUser, CognitoUserPool } from '@mira-avant/mtlibs';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { COGNITO_USER_PAGE_SIZE, CognitoService } from '../../../../services/cognito.service';
import { SearchFormComponent } from '../../../forms/standard/search-form/search-form.component';

@Component({
	selector: 'app-user-list',
	standalone: true,
	imports: [AngularModule, ListComponent, ZorroModule, SearchFormComponent],
	templateUrl: './user-list.component.html',
	styleUrl: './user-list.component.scss',
})
export class UserListComponent {
	readonly loading = signal<boolean>(false);
	readonly users = signal<CognitoUser[]>([]);
	readonly totalUsers = signal<number>(0);
	readonly selectedUserPoolId = signal<string | undefined>(undefined);

	// Pre-resolved display names: uuid → name
	readonly displayNames = signal<Record<string, string>>({});

	private sub: Subscription | undefined;

	readonly COGNITO_USER_PAGE_SIZE = COGNITO_USER_PAGE_SIZE;

	constructor(
		public cognitoService: CognitoService,
		public nav: NavigationService,
		public clipboardService: ClipboardService,
		public adminService: AdminService
	) {}

	ngOnInit(): void {
		this.sub = this.adminService.selectedUserPool$.subscribe((selectedUserPool: CognitoUserPool | undefined) => {
			if (!selectedUserPool?.id) {
				console.warn('No selected user pool');
				this.selectedUserPoolId.set(undefined);
				this.users.set([]);
				this.totalUsers.set(0);
				this.displayNames.set({});
				return;
			}

			this.selectedUserPoolId.set(selectedUserPool.id);
			this.loadPageOfUsers(selectedUserPool.id, 1);
		});
	}

	private async loadPageOfUsers(poolId: string, pageNumber: number): Promise<void> {
		this.loading.set(true);

		try {
			const users = await this.cognitoService.listUsers(poolId, {
				pageNumber,
				pageSize: this.COGNITO_USER_PAGE_SIZE,
			});

			this.users.set(users);

			// Pre-resolve display names
			await this.resolveDisplayNames(users, poolId);

			const total = await this.cognitoService.getTotalUsers(poolId);
			this.totalUsers.set(total);
		} catch (error) {
			console.error('Error loading users:', error);
			this.users.set([]);
			this.totalUsers.set(0);
			this.displayNames.set({});
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Resolves display names for all users using the real service method (Promise-based).
	 */
	private async resolveDisplayNames(users: CognitoUser[], poolId: string): Promise<void> {
		const map: Record<string, string> = {};

		await Promise.all(
			users.map(async user => {
				try {
					const name = await this.cognitoService.getDisplayName({
						cognito_uuid: user.uuid,
						userPoolId: poolId,
					});

					map[user.uuid] = name?.trim() || user.email || user.username || user.uuid || 'Unknown';
				} catch (err) {
					console.warn(`Could not resolve display name for ${user.uuid}`, err);
					map[user.uuid] = user.email || user.username || user.uuid || 'Unknown';
				}
			})
		);

		this.displayNames.set(map);
	}

	public async onPageChange(pageNumber: number): Promise<void> {
		const poolId = this.adminService.selectedUserPool$.value?.id;
		if (!poolId) {
			console.warn('No poolId provided');
			return;
		}

		await this.loadPageOfUsers(poolId, pageNumber);
	}

	public select(user: CognitoUser): void {
		const poolId = this.adminService.selectedUserPool$.value?.id;
		if (!poolId) return;

		this.nav.navigate([
			'my',
			'admin',
			'iam',
			{
				userPoolUuid: poolId,
				cognitoUuid: user.uuid,
			},
		]);
	}

	public edit(user: CognitoUser): void {
		// Currently unused in template
	}

	public onSearchFormSubmit$($event: any): void {
		const poolId = this.adminService.selectedUserPool$.value?.id;
		if (!poolId) {
			throw new Error('No user pool selected');
		}

		if (!$event.search) {
			this.loadPageOfUsers(poolId, 1);
			return;
		}

		this.cognitoService
			.searchUsers(poolId, $event.search)
			.then(async (users: CognitoUser[]) => {
				this.users.set(users);
				await this.resolveDisplayNames(users, poolId);
				this.totalUsers.set(users.length);
			})
			.catch(error => {
				console.error('Search failed:', error);
				this.users.set([]);
				this.totalUsers.set(0);
				this.displayNames.set({});
			});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
